import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ExternalLink, ArrowRight, Smartphone, Brain, Globe, Sparkles } from 'lucide-react';
import Section from '../components/Section';

const Projects: React.FC = () => {
  // Animasyon Varyantları
  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
  };

  return (
    <>
      {/* 1. HERO SECTION: Modern ve Bütünleşik Tasarım */}
      <section className="relative pt-32 pb-20 bg-primary-900 overflow-hidden">
        {/* Arka plan efektleri */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden opacity-20 pointer-events-none">
            <div className="absolute top-[-10%] right-[20%] w-[600px] h-[600px] bg-primary-400 rounded-full blur-[120px]"></div>
            <div className="absolute bottom-[-10%] left-[-10%] w-[400px] h-[400px] bg-white rounded-full blur-[100px]"></div>
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
          >
            <h1 className="text-4xl md:text-6xl font-display font-bold mb-6 text-white tracking-tight">
              Projelerimiz
            </h1>
            <p className="text-lg md:text-xl mb-8 max-w-2xl mx-auto text-primary-100 leading-relaxed">
              Spor teknolojilerinde sınırları zorluyor, yapay zeka ile geleceğin çözümlerini bugünden tasarlıyoruz.
            </p>
          </motion.div>
        </div>
      </section>

      {/* 2. FEATURED PROJECT - EXEREYES */}
      <Section className="py-20 -mt-10 relative z-20">
        <motion.div 
          className="bg-white rounded-3xl shadow-2xl shadow-primary-900/10 overflow-hidden border border-gray-100"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <div className="grid grid-cols-1 lg:grid-cols-2">
            
            {/* Sol Taraf: Görsel / Showcase */}
            <div className="relative min-h-[400px] lg:min-h-full bg-gradient-to-br from-gray-900 to-primary-900 flex items-center justify-center p-10 overflow-hidden group">
              {/* Arka plan dekoru */}
              <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10"></div>
              <div className="absolute top-0 right-0 w-64 h-64 bg-primary-500 rounded-full blur-3xl opacity-20 group-hover:opacity-30 transition-opacity duration-500"></div>
              
              {/* Logo / Görsel */}
              <motion.img
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.5 }}
                src="/exereyes_logo.jpg"
                alt="Exereyes Logo"
                className="relative z-10 max-w-[280px] w-full rounded-2xl shadow-2xl shadow-black/50"
              />
            </div>

            {/* Sağ Taraf: İçerik */}
            <div className="p-8 md:p-12 lg:p-16 flex flex-col justify-center">
              <div className="flex items-center gap-2 mb-6">
                <span className="px-4 py-1.5 bg-primary-50 text-primary-600 rounded-full text-sm font-bold tracking-wide uppercase flex items-center gap-2">
                  <Sparkles size={14} />
                  Öne Çıkan Proje
                </span>
              </div>

              <h2 className="text-4xl font-display font-bold text-gray-900 mb-6">
                Exereyes
              </h2>
              
              <p className="text-gray-600 text-lg mb-8 leading-relaxed">
                Yapay zeka destekli dijital sağlık asistanınız. Exereyes, evinizdeki nesneleri spor ekipmanına dönüştürerek, zamandan ve mekandan bağımsız, tamamen size özel bir egzersiz deneyimi sunar.
              </p>

              <div className="space-y-6 mb-10">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-blue-50 rounded-xl text-blue-600 shrink-0">
                    <Smartphone size={24} />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">Kamera Destekli Analiz</h3>
                    <p className="text-gray-500 text-sm">Telefon kamerasıyla ortamı tarar ve size en uygun egzersiz alanını yaratır.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="p-3 bg-purple-50 rounded-xl text-purple-600 shrink-0">
                    <Brain size={24} />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">AI Kişiselleştirme</h3>
                    <p className="text-gray-500 text-sm">Yaş, sağlık durumu ve hedeflerinize göre anlık planlama yapar.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="p-3 bg-teal-50 rounded-xl text-teal-600 shrink-0">
                    <Globe size={24} />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">Toplumsal Kapsayıcılık</h3>
                    <p className="text-gray-500 text-sm">Dezavantajlı gruplar dahil herkes için eşit sağlık fırsatı sunar.</p>
                  </div>
                </div>
              </div>

              <div>
                <Link
                  to="/exereyes"
                  className="group inline-flex items-center gap-2 bg-gray-900 text-white px-8 py-4 rounded-xl font-semibold text-lg hover:bg-primary-600 transition-all duration-300 shadow-lg shadow-gray-900/20 hover:shadow-primary-600/30"
                >
                  Projeyi İncele
                  <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </div>
          </div>
        </motion.div>
      </Section>
    </>
  );
};

export default Projects;